import { useState } from "react";
import { buildPlatformSummary } from "@/lib/platformSummary";
import { formatRelativeTime } from "@/lib/format";
import type { PlatformPulse } from "@/types";

interface PlatformPulseCardProps {
  pulse: PlatformPulse;
  onRefresh?: () => void;
  refreshing?: boolean;
  onOpenStorage?: () => void;
}

export function PlatformPulseCard({
  pulse,
  onRefresh,
  refreshing,
  onOpenStorage,
}: PlatformPulseCardProps) {
  const summary = buildPlatformSummary(pulse);

  return (
    <article
      className={`platform-pulse platform-pulse--${pulse.status}`}
      aria-label={`Platform status: ${summary.headline}`}
    >
      <div className="platform-pulse__header">
        <div className="platform-pulse__intro">
          <div className="platform-pulse__eyebrow">Platform status</div>
          <h3 className={`platform-pulse__headline platform-pulse__headline--${pulse.status}`}>
            {summary.headline}
          </h3>
          <div className="platform-pulse__summary">
            {pulse.aiSummary ? (
              <p className="platform-pulse__summary-line">{pulse.aiSummary}</p>
            ) : (
              summary.lines.map((line) => (
                <p key={line} className="platform-pulse__summary-line">
                  {line}
                </p>
              ))
            )}
          </div>
          {pulse.live ? (
            <span className="platform-pulse__live">Live · Control Plane</span>
          ) : null}
        </div>
        {onRefresh ? (
          <button type="button" className="btn" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        ) : null}
      </div>

      <div className="platform-pulse__grid">
        <div className="platform-pulse__stat">
          <strong>
            {pulse.activeTenants}
            <span className="platform-pulse__stat-sub"> / {pulse.totalTenants}</span>
          </strong>
          <span>Active tenants</span>
        </div>
        <div
          className={`platform-pulse__stat platform-pulse__stat--${pulse.runtimeDoctorStatus === "healthy" ? "ok" : pulse.runtimeDoctorStatus}`}
        >
          <strong>{pulse.doctorSummary?.fail ?? 0}</strong>
          <span>
            Open issues
            {pulse.doctorSummary
              ? ` · ${pulse.doctorSummary.warn} watch`
              : ""}
          </span>
        </div>
        <div className="platform-pulse__stat">
          <strong>{formatRelativeTime(pulse.lastChecked)}</strong>
          <span>Last verified</span>
        </div>
        <div className="platform-pulse__stat">
          <strong>{pulse.deploymentsRunning}</strong>
          <span>Deployments running</span>
        </div>
        <div className="platform-pulse__stat">
          <strong>
            {pulse.lastBackup ? formatRelativeTime(pulse.lastBackup) : "None found"}
          </strong>
          <span>Last successful backup</span>
        </div>
        {pulse.tenantsDoctorScanned != null ? (
          <div className="platform-pulse__stat">
            <strong>{pulse.tenantsDoctorScanned}</strong>
            <span>Tenants verified</span>
          </div>
        ) : null}
        {pulse.storageHealth ? (
          <button
            type="button"
            className={`platform-pulse__stat platform-pulse__stat--button platform-pulse__stat--${
              pulse.storageHealth.band === "green"
                ? "ok"
                : pulse.storageHealth.band === "yellow" || pulse.storageHealth.band === "orange"
                  ? "degraded"
                  : pulse.storageHealth.band === "unknown"
                    ? "unknown"
                    : "critical"
            }`}
            title={pulse.storageHealth.recommendedCleanup[0] ?? undefined}
            onClick={onOpenStorage}
          >
            <strong>
              {pulse.storageHealth.band}
              {pulse.storageHealth.filesystemUsedPercent != null
                ? ` · ${pulse.storageHealth.filesystemUsedPercent}%`
                : ""}
              {pulse.storageHealth.deployBlocked ? " · blocked" : ""}
            </strong>
            <span>Storage Health</span>
          </button>
        ) : null}
      </div>
    </article>
  );
}

interface PlatformPulseSectionProps {
  pulse: PlatformPulse | null;
  loading: boolean;
  error: string | null;
  needsAuth: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
  onOpenStorage?: () => void;
}

export function PlatformPulseSection({
  pulse,
  loading,
  error,
  needsAuth,
  refreshing,
  onRefresh,
  onLogin,
  onOpenStorage,
}: PlatformPulseSectionProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  if (needsAuth) {
    return (
      <div className="glass-panel platform-pulse-auth">
        <h3 className="platform-pulse-auth__title">Sign in to load Platform Pulse</h3>
        <p className="platform-pulse-auth__hint">
          Uses Control Plane admin credentials. Other sections stay empty until live data loads.
        </p>
        <form
          className="platform-pulse-auth__form"
          onSubmit={(e) => {
            e.preventDefault();
            setSigningIn(true);
            setLoginError(null);
            void onLogin(email, password)
              .catch((err) => {
                setLoginError(err instanceof Error ? err.message : "Sign in failed");
              })
              .finally(() => setSigningIn(false));
          }}
        >
          <input
            type="email"
            className="filter-input"
            placeholder="Admin email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
          />
          <input
            type="password"
            className="filter-input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
          <button type="submit" className="btn btn-primary" disabled={signingIn}>
            {signingIn ? "Signing in…" : "Sign in"}
          </button>
        </form>
        {loginError ? <p className="platform-pulse-auth__error">{loginError}</p> : null}
      </div>
    );
  }

  if (loading && !pulse) {
    return (
      <div className="platform-pulse platform-pulse--loading" aria-busy="true">
        <p>Loading live platform pulse…</p>
      </div>
    );
  }

  if (error && !pulse) {
    return (
      <div className="glass-panel platform-pulse-error">
        <p>{error}</p>
        <button type="button" className="btn btn-primary" onClick={onRefresh}>
          Retry
        </button>
      </div>
    );
  }

  if (!pulse) {
    return null;
  }

  return <PlatformPulseCard pulse={pulse} onRefresh={onRefresh} refreshing={refreshing} onOpenStorage={onOpenStorage} />;
}
